import React from 'react';

const getCurrencySymbol = (currency) => {
  const symbols = {
    'GBP': '£',
    'USD': '$',
    'EUR': '€',
    'PLN': 'zł'
  };
  return symbols[currency] || currency || '£';
};

const formatCurrencyAmount = (amount, currency) => {
  if (!amount && amount !== 0) return 'N/A';
  const formatted = parseFloat(amount).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${getCurrencySymbol(currency)}${formatted}`;
};

/**
 * CurrencyDisplay Component
 * 
 * Displays amounts in the company's default currency with original currency in brackets
 * 
 * @param {Object} props
 * @param {number} props.displayAmount - Amount in company default currency
 * @param {string} props.defaultCurrency - Company default currency code (e.g., 'GBP')
 * @param {number} [props.originalAmount] - Amount in original invoice currency
 * @param {string} [props.originalCurrency] - Original invoice currency code
 * @param {number} [props.exchangeRate] - Exchange rate used for conversion
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.showRate] - Whether to show exchange rate (default: true)
 */
export default function CurrencyDisplay({ 
  displayAmount, 
  defaultCurrency = 'GBP',
  originalAmount, 
  originalCurrency,
  exchangeRate,
  className = '',
  showRate = true
}) {
  // If no original amount or same currency, just show the display amount
  if (!originalAmount || !originalCurrency || originalCurrency === defaultCurrency) {
    return (
      <span className={className}>
        {formatCurrencyAmount(displayAmount, defaultCurrency)}
      </span>
    );
  }

  // Show display amount with original in brackets
  return (
    <span className={className}>
      {formatCurrencyAmount(displayAmount, defaultCurrency)}
      {' '}
      <span className="text-gray-500 text-sm">
        ({formatCurrencyAmount(originalAmount, originalCurrency)}
        {showRate && exchangeRate && exchangeRate !== 1 && (
          <> @ {parseFloat(exchangeRate).toFixed(4)}</>
        )}
        )
      </span>
    </span>
  );
}

// Export utility functions for use in other components
export { getCurrencySymbol, formatCurrencyAmount };
